import { memo } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ISortableColumnHeaderProps } from '@/@types/tableSort';

/**
 * Shared column header with sort indicators.
 * - Non-sortable columns: plain label text
 * - Sortable (inactive): label + muted ArrowUpDown icon
 * - Sortable (active): label + accent ArrowUp or ArrowDown icon
 *
 * Renders only the label + icon — does NOT render wrapper elements.
 */
export const SortableColumnHeader = memo(function SortableColumnHeader({
  column,
  sortState,
  onToggleSort,
}: ISortableColumnHeaderProps) {
  const isSortable = column.sortType !== undefined;
  const isActive = isSortable && sortState.sortBy === column.id;
  const isAsc = isActive && sortState.direction === 'asc';

  if (!isSortable) {
    return (
      <span className="text-xs font-medium text-table-header-text uppercase tracking-wider">
        {column.label}
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-sort={isActive ? (isAsc ? 'ascending' : 'descending') : 'none'}
      onClick={() => onToggleSort(column.id)}
      className="inline-flex items-center gap-1 cursor-pointer select-none text-left group"
    >
      <span className="text-xs font-medium text-table-header-text uppercase tracking-wider">
        {column.label}
      </span>
      {isActive ? (
        <>
          {isAsc ? (
            <ArrowUp className="h-3.5 w-3.5 text-table-header-text" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-table-header-text" />
          )}
          <span className="sr-only">sorted {isAsc ? 'ascending' : 'descending'}</span>
        </>
      ) : (
        <ArrowUpDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground/40',
            'group-hover:text-muted-foreground transition-colors'
          )}
        />
      )}
    </button>
  );
});
